// eventTypeLabels.ts — the words for LexiStar event types (ST-00280, 2026-10-20)
//
// ONE source, two byte-identical copies (the client's Docker build context is client/ and the
// server's image copies only server/, so neither can import the other or a workspace package):
//   server/modules/lexistar/events/eventTypeLabels.ts   the source (notification texts)
//   client/src/utils/eventTypeLabels.ts                  a copy (every page that names an event)
// client/src/utils/eventTypeLabels.copy.test.ts fails when the two differ: edit the server file,
// then `cp` it over the client one. Pure TypeScript with no imports: no React, no DOM, no Node,
// no database.
//
// An event type is a code, CATEGORY.NAME (`TRIP.ARRIVED_SOURCE`, @lexistar/domain
// EVENT_CATALOGUE). People never read the code: every page, notification, e-mail and driver /
// controller message names the type with eventTypeLabel ("Arrived at source"). The code stays
// for the places power users need it (a rule's "When" picker shows it under the words, the
// webhook deliveries keep it as the payload's type).
//
// EVERY catalogue type has a label: the client's eventTypeLabels.test.ts and the server's
// lexistar-event-labels.test.ts read @lexistar/domain's catalogue and fail on a type without
// one, so a new type cannot ship as a raw code. A code that is not here (a type a later engine
// emits, a seeded rule's planned type) is still worded, by humaniseEventType: "Arrived at
// source" from TRIP.ARRIVED_SOURCE, "Something new" from NEWCAT.SOMETHING_NEW.

/**
 * @lexistar/domain EVENT_CATALOGUE (M1 + M2 + M3 + M4 + M5), in its order. The webhook event
 * picker offers exactly these (webhookLogic.ts).
 */
export const CATALOGUE_EVENT_TYPES: readonly string[] = [
  'GEOFENCE.ENTERED',
  'GEOFENCE.EXITED',
  'GEOFENCE.DWELL_EXCEEDED',
  'GEOFENCE.DWELL_TOO_SHORT',
  'GEOFENCE.NOGO_ENTERED',
  'GEOFENCE.AFTER_HOURS',
  'MOTION.EXCESSIVE_IDLE',
  'MOTION.OVERSPEED',
  'DEVICE.NO_SIGNAL',
  'DEVICE.SIGNAL_RESTORED',
  'TRIP.DISPATCHED',
  'TRIP.ACKNOWLEDGED',
  'TRIP.NOT_ACKNOWLEDGED',
  'TRIP.ARRIVED_SOURCE',
  'TRIP.DEPARTED_SOURCE',
  'TRIP.LOADED',
  'TRIP.ARRIVED_DESTINATION',
  'TRIP.OFFLOADED',
  'TRIP.DEPARTED_DESTINATION',
  'TRIP.COMPLETED',
  'TRIP.RECONCILED',
  'TRIP.EXCEPTION_HOLD',
  'TRIP.CANCELLED',
  'TRIP.ABORTED',
  'TRIP.ETA_RISK',
  'TRIP.DELAY_AT_SITE',
  'TRIP.UNPLANNED_LOAD_DETECTED',
  'TRIP.UNALLOCATED_MOVEMENT',
  'TRIP.DISTANCE_ABOVE_ROUTE_NORM',
  'TRIP.CREATED_RETROSPECTIVELY',
  'RUN.STARTED',
  'RUN.TRIP_APPENDED',
  'RUN.TRIP_ACTIVATED',
  'RUN.CLOSED',
  'ROUTE.DEVIATION_OPENED',
  'ROUTE.DEVIATION_APPROVED',
  'ROUTE.DEVIATION_REJECTED',
  'ROUTE.DEVIATION_CLOSED',
  'ROUTE.DEVIATION_VOIDED',
  'ROUTE.DEVIATION_ESCALATED',
  'ROUTE.STOP_WHILE_DEVIATING',
  'ROUTE.ADVISORY_PUBLISHED',
  'ROUTE.ADVISORY_EXPIRED',
  'ROUTE.PROPOSED_ROUTE_USED',
  'FUEL.REFUEL_DETECTED',
  'FUEL.REFUEL_UNAUTHORISED_LOCATION',
  'FUEL.LOSS_SUSPECTED',
  'FUEL.BURN_INDEX_HIGH',
  'FUEL.OVERCONSUMPTION_PROJECTED',
  'FUEL.ALLOWANCE_REACHED',
  'FUEL.OVERCONSUMPTION',
  'WB.TICKET_RECEIVED',
  'WB.MATCHED',
  'WB.UNMATCHED',
  'WB.VARIANCE_EXCEEDED',
  'WB.TICKET_WITHOUT_PRESENCE',
  'WB.BYPASS',
  'WB.REGISTRATION_UNRESOLVED',
  'WB.PRESENCE_VERIFIED',
  'TRIP.REPLANNED',
  'TRIP.SIGNED_OFF',
  'TRIP.AMENDED',
  'ROUTE.DEVIATION_REASON_RECEIVED',
  'ROUTE.DEVIATION_SLA_BREACHED',
  'COMMS.EMERGENCY_KEYWORD',
  'COMMS.DRIVER_PROBLEM_REPORTED',
  'COMMS.UNMATCHED_INBOUND',
  'COMMS.OPT_OUT',
  'COMMS.DELIVERY_DEAD_LETTERED',
  'ASR.REVIEW_ITEM_OPENED',
  'ASR.REVIEW_ITEM_RESOLVED',
  'ASR.RECOMPUTE_COMPUTED',
  'ASR.RECOMPUTE_PUBLISHED',
  'ASR.RECOMPUTE_DISCARDED',
  'ASR.RECOMPUTE_FAILED',
  'ASR.DECLARATION_PUBLISHED',
  'ASR.DECLARATION_RETRACTED',
  'ASR.PARALLEL_RUN_SIGNED_OFF',
  'ASR.CERTIFICATE_ISSUED',
  'ASR.PARALLEL_RUN_OVERRIDE_REQUESTED',
  'ASR.PARALLEL_RUN_OVERRIDE_REPLACED',
  'ASR.PARALLEL_RUN_OVERRIDE_EXPIRED',
  'ASR.PARALLEL_RUN_OVERRIDE_DROPPED',
  'ANALYTICS.DWELL_EXCEEDED',
  // M5 (ST-00205): the trips API's dispatch requests (the driver is messaged from them)
  'TRIP.DISPATCH_REQUESTED',
  'RUN.TRIP_APPEND_REQUESTED',
];

/**
 * Plain-language names of the event types: every catalogue type, plus the types outside the
 * catalogue that people still see (the rules migration 027 seeds for planned types, and the
 * in-app report deliveries). Sentence case; each reads on its own, without its category.
 */
export const EVENT_TYPE_LABELS: Readonly<Record<string, string>> = {
  // GEOFENCE.* — sites and zones
  'GEOFENCE.ENTERED': 'Entered a zone',
  'GEOFENCE.EXITED': 'Left a zone',
  'GEOFENCE.DWELL_EXCEEDED': 'Stayed too long in a zone',
  'GEOFENCE.DWELL_TOO_SHORT': 'Left a zone too soon',
  'GEOFENCE.NOGO_ENTERED': 'Entered a no-go zone',
  'GEOFENCE.AFTER_HOURS': 'In a zone after hours',
  // MOTION.* — driving
  'MOTION.EXCESSIVE_IDLE': 'Idling too long',
  'MOTION.OVERSPEED': 'Overspeeding',
  'MOTION.UNSCHEDULED_STOP': 'Unscheduled stop',
  // DEVICE.* — tracking devices
  'DEVICE.NO_SIGNAL': 'Tracker lost signal',
  'DEVICE.SIGNAL_RESTORED': 'Tracker signal restored',
  // TRIP.* — the trip's life
  'TRIP.DISPATCH_REQUESTED': 'Trip dispatch requested',
  'TRIP.DISPATCHED': 'Trip dispatched',
  'TRIP.ACKNOWLEDGED': 'Trip accepted by the driver',
  'TRIP.NOT_ACKNOWLEDGED': 'Trip not accepted by the driver',
  'TRIP.ARRIVED_SOURCE': 'Arrived at source',
  'TRIP.DEPARTED_SOURCE': 'Departed from source',
  'TRIP.LOADED': 'Loaded',
  'TRIP.ARRIVED_DESTINATION': 'Arrived at destination',
  'TRIP.OFFLOADED': 'Offloaded',
  'TRIP.DEPARTED_DESTINATION': 'Departed from destination',
  'TRIP.COMPLETED': 'Trip completed',
  'TRIP.RECONCILED': 'Trip reconciled',
  'TRIP.EXCEPTION_HOLD': 'Trip on hold for an exception',
  'TRIP.CANCELLED': 'Trip cancelled',
  'TRIP.ABORTED': 'Trip aborted',
  'TRIP.ETA_RISK': 'ETA at risk',
  'TRIP.DELAY_AT_SITE': 'Delayed at a site',
  'TRIP.UNPLANNED_LOAD_DETECTED': 'Unplanned load detected',
  'TRIP.UNALLOCATED_MOVEMENT': 'Moved without a trip',
  'TRIP.DISTANCE_ABOVE_ROUTE_NORM': 'Distance above the route norm',
  'TRIP.CREATED_RETROSPECTIVELY': 'Trip created afterwards',
  'TRIP.REPLANNED': 'Trip re-planned',
  'TRIP.SIGNED_OFF': 'Trip signed off',
  'TRIP.AMENDED': 'Trip record amended',
  // RUN.* — chained trips
  'RUN.STARTED': 'Run started',
  'RUN.TRIP_APPEND_REQUESTED': 'Next load requested',
  'RUN.TRIP_APPENDED': 'Next load added to the run',
  'RUN.TRIP_ACTIVATED': 'Next trip of the run started',
  'RUN.CLOSED': 'Run closed',
  // ROUTE.* — routes, deviations and advisories
  'ROUTE.DEVIATION_OPENED': 'Left the approved route',
  'ROUTE.DEVIATION_APPROVED': 'Deviation approved',
  'ROUTE.DEVIATION_REJECTED': 'Deviation rejected',
  'ROUTE.DEVIATION_CLOSED': 'Deviation closed',
  'ROUTE.DEVIATION_VOIDED': 'Deviation voided',
  'ROUTE.DEVIATION_ESCALATED': 'Deviation escalated',
  'ROUTE.DEVIATION_REASON_RECEIVED': 'Driver gave a reason for the deviation',
  'ROUTE.DEVIATION_SLA_BREACHED': 'Deviation not handled in time',
  'ROUTE.STOP_WHILE_DEVIATING': 'Stopped while off route',
  'ROUTE.ADVISORY_PUBLISHED': 'Route advisory published',
  'ROUTE.ADVISORY_EXPIRED': 'Route advisory ended',
  'ROUTE.PROPOSED_ROUTE_USED': 'Proposed route used',
  // FUEL.*
  'FUEL.REFUEL_DETECTED': 'Refuel detected',
  'FUEL.REFUEL_UNAUTHORISED_LOCATION': 'Refuel at an unauthorised place',
  'FUEL.LOSS_SUSPECTED': 'Fuel loss suspected',
  'FUEL.BURN_INDEX_HIGH': 'Burn index high',
  'FUEL.OVERCONSUMPTION_PROJECTED': 'Over-consumption projected',
  'FUEL.ALLOWANCE_REACHED': 'Fuel allowance reached',
  'FUEL.OVERCONSUMPTION': 'Over-consumption',
  // WB.* — weighbridge tickets
  'WB.TICKET_RECEIVED': 'Weighbridge ticket received',
  'WB.MATCHED': 'Weighbridge ticket matched to a trip',
  'WB.UNMATCHED': 'Weighbridge ticket without a trip',
  'WB.VARIANCE_EXCEEDED': 'Weighbridge variance exceeded',
  'WB.TICKET_WITHOUT_PRESENCE': 'Ticket while the truck was not at the weighbridge',
  'WB.BYPASS': 'Weighbridge bypassed',
  'WB.REGISTRATION_UNRESOLVED': 'Ticket registration not recognised',
  'WB.PRESENCE_VERIFIED': 'Truck seen at the weighbridge',
  // COMMS.* — driver messages
  'COMMS.EMERGENCY_KEYWORD': 'Driver sent an emergency word',
  'COMMS.DRIVER_PROBLEM_REPORTED': 'Driver reported a problem',
  'COMMS.UNMATCHED_INBOUND': 'Message from an unknown number',
  'COMMS.OPT_OUT': 'Driver stopped messages',
  'COMMS.DELIVERY_DEAD_LETTERED': 'Message could not be delivered',
  // ASR.* — assurance
  'ASR.REVIEW_ITEM_OPENED': 'Review item opened',
  'ASR.REVIEW_ITEM_RESOLVED': 'Review item resolved',
  'ASR.RECOMPUTE_COMPUTED': 'Recompute ready to check',
  'ASR.RECOMPUTE_PUBLISHED': 'Recompute published',
  'ASR.RECOMPUTE_DISCARDED': 'Recompute discarded',
  'ASR.RECOMPUTE_FAILED': 'Recompute failed',
  'ASR.DECLARATION_PUBLISHED': 'Declaration published',
  'ASR.DECLARATION_RETRACTED': 'Declaration withdrawn',
  'ASR.PARALLEL_RUN_OVERRIDE_REQUESTED': 'Sign-off override requested',
  'ASR.PARALLEL_RUN_OVERRIDE_REPLACED': 'Sign-off override replaced',
  'ASR.PARALLEL_RUN_OVERRIDE_EXPIRED': 'Sign-off override expired',
  'ASR.PARALLEL_RUN_OVERRIDE_DROPPED': 'Sign-off override dropped',
  'ASR.PARALLEL_RUN_SIGNED_OFF': 'Parallel run signed off',
  'ASR.CERTIFICATE_ISSUED': 'Certificate issued',
  // ANALYTICS.*
  'ANALYTICS.DWELL_EXCEEDED': 'Site dwell over the limit',
  // Outside the catalogue: migration 027's rules for planned types, and report deliveries
  'SECURITY.PANIC': 'Panic button pressed',
  'SECURITY.POWER_DISCONNECT': 'Tracker power cut',
  'DRIVER.DOCUMENT_EXPIRING': 'Driver document expiring',
  'DRIVER.DOCUMENT_EXPIRED': 'Driver document expired',
  'MAINT.DOCUMENT_EXPIRING': 'Vehicle document expiring',
  'REPORT.DELIVERED': 'Report delivered',
};

/** Plain-language names of the event categories (a group heading), in picker order. */
export const EVENT_CATEGORY_LABELS: Readonly<Record<string, string>> = {
  TRIP: 'Trips',
  ROUTE: 'Routes and deviations',
  RUN: 'Runs',
  WB: 'Weighbridge',
  FUEL: 'Fuel',
  GEOFENCE: 'Sites and zones',
  MOTION: 'Driving',
  DEVICE: 'Tracking devices',
  COMMS: 'Driver messages',
  ASR: 'Assurance',
  ANALYTICS: 'Analytics',
  SECURITY: 'Security',
  DRIVER: 'Drivers',
  MAINT: 'Maintenance',
  REPORT: 'Reports',
};

/** The category in a sentence: "all trip events", "every weighbridge event". */
export const EVENT_CATEGORY_NOUNS: Readonly<Record<string, string>> = {
  TRIP: 'trip',
  ROUTE: 'route and deviation',
  RUN: 'run',
  WB: 'weighbridge',
  FUEL: 'fuel',
  GEOFENCE: 'site and zone',
  MOTION: 'driving',
  DEVICE: 'tracking device',
  COMMS: 'driver message',
  ASR: 'assurance',
  ANALYTICS: 'analytics',
  SECURITY: 'security',
  DRIVER: 'driver',
  MAINT: 'maintenance',
  REPORT: 'report',
};

/** Abbreviations that stay in capitals ("ETA at risk", "SLA breached"). */
const ACRONYMS: ReadonlySet<string> = new Set(['SLA', 'ETA', 'GPS', 'ID', 'API', 'SMS', 'POD', 'KM', 'WB']);

const sentence = (words: readonly string[]): string => {
  const text = words
    .map((w) => (ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : w.toLowerCase()))
    .join(' ');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};

/** `TRIP.ARRIVED_SOURCE` -> `TRIP`; a code without a dot is its own category. */
export function eventCategoryOf(type: string): string {
  const dot = type.indexOf('.');
  return dot < 0 ? type : type.slice(0, dot);
}

/**
 * Words for a code that has no label: the name part in sentence case, "arrived" / "departed"
 * read as "arrived at" / "departed from". `TRIP.ARRIVED_SOURCE` -> "Arrived at source",
 * `NEWCAT.SOMETHING_ELSE` -> "Something else", `DIGEST` -> "Digest".
 */
export function humaniseEventType(type: unknown): string {
  const code = String(type ?? '').trim();
  if (!code) return '';
  const dot = code.indexOf('.');
  const name = dot < 0 ? code : code.slice(dot + 1);
  const words = (name || code).split(/[_\s.]+/).filter(Boolean);
  const first = (words[0] || '').toUpperCase();
  if (words.length > 1 && first === 'ARRIVED' && words[1]?.toUpperCase() !== 'AT') words.splice(1, 0, 'at');
  if (words.length > 1 && first === 'DEPARTED' && words[1]?.toUpperCase() !== 'FROM') words.splice(1, 0, 'from');
  return sentence(words) || code;
}

/** The words for an event type: its label, else the humanised code. Empty for no type. */
export function eventTypeLabel(type: unknown): string {
  const code = String(type ?? '').trim();
  if (!code) return '';
  return EVENT_TYPE_LABELS[code] ?? humaniseEventType(code);
}

/**
 * The words for an event type inside a sentence ("When arrived at source, notify…", "All trip
 * events and weighbridge bypassed"): the label with its first letter in lower case, unless it
 * starts with an abbreviation ("ETA at risk"). Empty for no type.
 */
export function eventTypePhrase(type: unknown): string {
  const label = eventTypeLabel(type);
  return /^[A-Z]{2,}\b/.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1);
}

/** The words for a category (`TRIP` -> "Trips"); an unknown one in sentence case. */
export function eventCategoryLabel(category: unknown): string {
  const c = String(category ?? '').trim();
  if (!c) return '';
  return EVENT_CATEGORY_LABELS[c] ?? sentence(c.split(/[_\s]+/));
}

/** The category in a sentence (`TRIP` -> "trip"); an unknown one in lower case. */
export function eventCategoryNoun(category: unknown): string {
  const c = String(category ?? '').trim();
  return EVENT_CATEGORY_NOUNS[c] ?? c.replace(/_/g, ' ').toLowerCase();
}

/** Every CATEGORY.NAME token in a text (letters, digits and underscores after the dot). */
const CODE_IN_TEXT = /\b[A-Z][A-Z0-9]*\.[A-Z][A-Z0-9_]*\b/g;

/**
 * A stored text with the event codes in it worded: "TRIP.ARRIVED_SOURCE: JX12KL at Mine A" ->
 * "Arrived at source: JX12KL at Mine A". Only codes this module labels are replaced, so a
 * registration, a file name or a dotted reference is never touched. For notifications written
 * before the template variables were worded (their titles still carry the code).
 */
export function withEventLabels(text: unknown): string {
  if (text === null || text === undefined) return '';
  return String(text).replace(CODE_IN_TEXT, (code) => EVENT_TYPE_LABELS[code] ?? code);
}
